"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  pingImperialSessionAction,
  refreshAfterSessionChangeAction,
} from "@/lib/admin/session-actions";
import { toast } from "@/lib/notifications/toast";
import { useAdmin } from "@/lib/admin/context";

/**
 * SessionWatcher — wykrywa zmiany stanu Imperial SSO session i refreshuje
 * React-CMS gdy user wyloguje się lub przelogowuje w Imperial admin.
 *
 * **Mechanizm detekcji** (3 trigery):
 *   1. **Polling co 30s** — periodyczny ping `pingImperialSessionAction()`.
 *      Wykrywa logout w innej karcie po max 30s opóźnieniu.
 *   2. **`visibilitychange`** — gdy user wraca na tab React-CMS po przełączeniu
 *      się gdzie indziej (np. logout w Imperial admin). Immediate check.
 *   3. **`focus`** — gdy okno przejmuje focus (analogiczne do visibilitychange,
 *      pokrywa edge case fullscreen / multi-monitor).
 *
 * **Reakcja na zmianę:**
 *   - Logout (loggedIn: true -> false) -> toast "Sesja Imperial wygasła" +
 *     `refreshAfterSessionChangeAction()` -> Server Components re-renderują
 *     z anonymous user'em (admin top bar znika).
 *   - User switch (userId zmienia) -> toast "Zmiana użytkownika" + refresh.
 *   - Login (loggedIn: false -> true) -> toast "Zalogowany jako {login}" + refresh.
 *
 * **Performance:**
 *   - Polling co 30s = 120 req/h. Server Action lekki (DB query + cookie check).
 *   - Pause polling when document.hidden (browser tab inactive) — save resources.
 *
 * **Initial state** podawane jako prop z PublicLayout (server-side state).
 * Client porównuje przy każdym pingu.
 */

export type SessionWatcherProps = {
  /** Initial logged_in state przekazany z server-side render. */
  initialLoggedIn: boolean;
  /** Initial user id (jeżeli zalogowany). NULL dla anonymous. */
  initialUserId: number | null;
};

/** Polling interval w ms. */
const POLL_INTERVAL = 30_000;

export function SessionWatcher({ initialLoggedIn, initialUserId }: SessionWatcherProps) {
  const router = useRouter();
  const { t } = useAdmin();
  /* Ref żeby callback'i miały zawsze aktualne wartości bez triggerowania
     re-mount'u (interval/event listeners stale closure). */
  const knownStateRef = useRef({
    loggedIn: initialLoggedIn,
    userId: initialUserId,
  });
  /* Trzymamy `t` w ref — `useEffect` poniżej tworzy stabilny callback bez
     re-mount'u na zmianę locale. Zmiana języka pojawia się natychmiast w
     kolejnym toaście (bez restart polling'u). */
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  useEffect(() => {
    knownStateRef.current = { loggedIn: initialLoggedIn, userId: initialUserId };
  }, [initialLoggedIn, initialUserId]);

  useEffect(() => {
    let cancelled = false;

    const handleStateChange = (next: { loggedIn: boolean; userId: number | null }) => {
      if (cancelled) return;
      const prev = knownStateRef.current;

      /* Brak zmiany — skip. */
      if (prev.loggedIn === next.loggedIn && prev.userId === next.userId) {
        return;
      }

      knownStateRef.current = next;

      /* Diagnose change + odpowiedni toast. */
      const tr = tRef.current.sessionWatcher;
      if (prev.loggedIn && !next.loggedIn) {
        toast.warning(tr.toastExpired);
      } else if (!prev.loggedIn && next.loggedIn) {
        toast.success(tr.toastLoggedIn(next.userId));
      } else if (prev.userId !== next.userId) {
        toast.info(tr.toastUserChanged(next.userId));
      }

      /* Trigger server-side re-render — PublicLayout ponownie wywoła
         getImperialSession() i zaktualizuje user/scopes/unpublishedWindows. */
      refreshAfterSessionChangeAction().then(() => {
        if (!cancelled) router.refresh();
      });
    };

    const ping = async () => {
      if (cancelled) return;
      try {
        const result = await pingImperialSessionAction();
        handleStateChange({
          loggedIn: result.loggedIn,
          userId: result.userId ?? null,
        });
      } catch (e) {
        /* Network error / Server Action fail — silent. Nie chcemy spamować
           toastami gdy backend offline. Następny ping spróbuje ponownie. */
        console.warn("[SessionWatcher] ping failed", e);
      }
    };

    /* (1) Polling co POLL_INTERVAL. setInterval, NIE setTimeout-loop bo
       chcemy stałą cadence (nie cumulative drift z czasem requestu). */
    const intervalId = window.setInterval(() => {
      if (!document.hidden) ping();
    }, POLL_INTERVAL);

    /* (2) visibilitychange — immediate check gdy tab z back-focusem. */
    const onVisibility = () => {
      if (!document.hidden) ping();
    };
    document.addEventListener("visibilitychange", onVisibility);

    /* (3) window focus — analogiczne (multi-monitor edge case). */
    const onFocus = () => ping();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [router]);

  /* Komponent niewidoczny — sama logika watcher'a w useEffect. */
  return null;
}
